import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { Variants } from "framer-motion";
import {
  Search,
  Globe,
  Network,
  LayoutGrid,
  List,
  Server,
  Activity,
  Cpu
} from "lucide-react";
import type { SubdomainInfo } from "./types";

interface SubdomainsTabProps {
  subdomains: SubdomainInfo[];
}

export default function SubdomainsTab({ subdomains }: SubdomainsTabProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  if (!subdomains || subdomains.length === 0) {
    return (
      <div className="py-12 border border-dashed border-gray-800 rounded-xl text-center text-gray-200 italic">
        No subdomains discovered.
      </div>
    );
  }

  // Calculate statistics
  const totalSubdomains = subdomains.length;
  const webSubdomains = subdomains.filter((s) => s.http_code !== null).length;
  const dnsResolved = totalSubdomains - webSubdomains;

  // Filter subdomains
  const filteredSubdomains = subdomains.filter((sub) => {
    const matchesSearch =
      sub.subdomain.toLowerCase().includes(search.toLowerCase()) ||
      sub.ip.toLowerCase().includes(search.toLowerCase());

    if (!matchesSearch) return false;

    if (statusFilter === "ALL") return true;
    if (statusFilter === "DNS") return sub.http_code === null;
    if (statusFilter === "2XX") return sub.http_code !== null && sub.http_code >= 200 && sub.http_code < 300;
    if (statusFilter === "3XX") return sub.http_code !== null && sub.http_code >= 300 && sub.http_code < 400;
    if (statusFilter === "4XX") return sub.http_code !== null && sub.http_code >= 400 && sub.http_code < 500;
    if (statusFilter === "5XX") return sub.http_code !== null && sub.http_code >= 500;

    return true;
  });

  // Paginate grid/table views
  const totalPages = Math.ceil(filteredSubdomains.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubdomains = filteredSubdomains.slice(startIndex, startIndex + itemsPerPage);

  // Status indicator style generators
  const getStatusDotClass = (code: number | null) => {
    if (code === null) return "bg-blue-500 shadow-[0_0_8px_#3b82f6]";
    if (code >= 200 && code < 300) return "bg-emerald-500 shadow-[0_0_8px_#10b981]";
    if (code >= 300 && code < 400) return "bg-amber-400 shadow-[0_0_8px_#fbbf24]";
    if (code >= 400 && code < 500) return "bg-orange-500 shadow-[0_0_8px_#f97316]";
    return "bg-red-500 shadow-[0_0_8px_#ef4444]";
  };

  const getStatusColor = (code: number | null) => {
    if (code === null) return "text-blue-400 bg-blue-500/10 border-blue-500/20";
    if (code >= 200 && code < 300) return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
    if (code >= 300 && code < 400) return "text-amber-400 bg-amber-500/10 border-amber-500/20";
    if (code >= 400 && code < 500) return "text-orange-400 bg-orange-500/10 border-orange-500/20";
    return "text-red-400 bg-red-500/10 border-red-500/20";
  };

  // Framer Motion Animation Variants
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.04
      }
    }
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { duration: 0.2 }
    },
    exit: { opacity: 0, transition: { duration: 0.15 } }
  };

  return (
    <div className="space-y-5">
      {/* Overview stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-850/30 border border-gray-700 rounded-xl p-3 flex items-center gap-3">
          <div className="bg-blue-500/10 p-2 rounded-lg text-blue-400 border border-blue-500/15">
            <Globe size={18} />
          </div>
          <div>
            <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest leading-none mb-1">Total Subdomains</p>
            <p className="text-base font-black text-white leading-none font-mono">{totalSubdomains}</p>
          </div>
        </div>
        <div className="bg-gray-850/30 border border-gray-700 rounded-xl p-3 flex items-center gap-3">
          <div className="bg-emerald-500/10 p-2 rounded-lg text-emerald-400 border border-emerald-500/15">
            <Network size={18} />
          </div>
          <div>
            <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest leading-none mb-1">Web Servers</p>
            <p className="text-base font-black text-emerald-450 leading-none font-mono">{webSubdomains}</p>
          </div>
        </div>
        <div className="bg-gray-850/30 border border-gray-700 rounded-xl p-3 flex items-center gap-3">
          <div className="bg-purple-500/10 p-2 rounded-lg text-purple-400 border border-purple-500/15">
            <Cpu size={18} />
          </div>
          <div>
            <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest leading-none mb-1">DNS Resolved Only</p>
            <p className="text-base font-black text-purple-400 leading-none font-mono">{dnsResolved}</p>
          </div>
        </div>
      </div>

      {/* Control bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-850/40 p-3 rounded-xl border border-gray-800">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-200 w-3.5 h-3.5" />
          <input
            type="text"
            placeholder="Search subdomains or IPs..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-4 py-1.5 text-xs text-white focus:border-orange-500 outline-none transition-all"
          />
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-gray-450 uppercase tracking-widest whitespace-nowrap">Filter:</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="bg-gray-900 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 focus:border-orange-500 outline-none cursor-pointer"
            >
              <option value="ALL">All Statuses</option>
              <option value="2XX">HTTP 2xx</option>
              <option value="3XX">HTTP 3xx</option>
              <option value="4XX">HTTP 4xx</option>
              <option value="5XX">HTTP 5xx</option>
              <option value="DNS">DNS Only</option>
            </select>
          </div>

          {/* Grid/Table Toggle */}
          <div className="flex items-center border border-gray-700 bg-gray-900 rounded-lg p-0.5 shrink-0">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded-md transition-colors ${viewMode === "grid" ? "bg-orange-600 text-white" : "text-gray-200 hover:text-white"
                }`}
              title="Animated Cards Grid"
            >
              <LayoutGrid size={13} />
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`p-1.5 rounded-md transition-colors ${viewMode === "table" ? "bg-orange-600 text-white" : "text-gray-200 hover:text-white"
                }`}
              title="Table Grid"
            >
              <List size={13} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content View with Animation Toggle */}
      <AnimatePresence mode="wait">
        {viewMode === "grid" ? (
          /* VIEW 1: PREMIUM ANIMATED NODE CARDS GRID */
          <motion.div
            key="grid-mode"
            variants={containerVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            {paginatedSubdomains.length > 0 ? (
              paginatedSubdomains.map((sub, index) => (
                <motion.div
                  key={sub.subdomain + index}
                  variants={cardVariants}
                  className={`bg-gray-850/20 border border-gray-700 rounded-xl p-4 flex flex-col justify-between shadow-md relative overflow-hidden transition-all duration-200 ${
                    sub.http_code === null ? "hover:border-blue-500/30" : sub.http_code >= 200 && sub.http_code < 300 ? "hover:border-emerald-500/30" : "hover:border-orange-500/30"
                  }`}
                >
                  {/* Subtle top light bar */}
                  {/* <div className={`absolute top-0 left-0 right-0 h-[2px] ${
                    sub.http_code === null ? "bg-blue-500" : sub.http_code >= 200 && sub.http_code < 300 ? "bg-emerald-500" : "bg-orange-500"
                  }`} /> */}

                  <div className="space-y-3">
                    {/* Header: Pulsing icon & Status indicator */}
                    <div className="flex items-center justify-between">
                      <div className="bg-gray-900 border border-gray-800 p-1.5 rounded-lg text-gray-200">
                        {sub.http_code ? (
                          <Server size={14} className="text-emerald-400" />
                        ) : (
                          <Globe size={14} className="text-blue-400" />
                        )}
                      </div>

                      {/* Active Status Badge */}
                      <span className={`text-[9px] font-mono font-extrabold uppercase px-2 py-0.5 rounded-full flex items-center gap-1.5 ${getStatusColor(sub.http_code)}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${getStatusDotClass(sub.http_code)}`} />
                        {sub.http_code ? `HTTP ${sub.http_code}` : "DNS Resolved"}
                      </span>
                    </div>

                    {/* Subdomain Text */}
                    <div className="space-y-1">
                      <p className="text-xs font-black text-gray-200 truncate leading-snug" title={sub.subdomain}>
                        {sub.subdomain}
                      </p>
                      <p className="text-[10px] font-mono text-gray-450 leading-none">
                        IP: {sub.ip}
                      </p>
                    </div>
                  </div>

                  {/* Footing tags */}
                  <div className="mt-4 pt-2.5 border-t border-gray-700 flex items-center justify-between text-[9px] font-bold text-gray-200">
                    <span className="font-mono bg-gray-900/50 px-1.5 py-0.2 rounded border border-gray-800 text-gray-200 uppercase tracking-wider">
                      {sub.service || "DNS"}
                    </span>
                    <span className="flex items-center gap-1 opacity-70">
                      <Activity size={10} className="text-orange-500" /> {sub.method}
                    </span>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div
                variants={cardVariants}
                className="col-span-full py-16 text-center text-gray-200 font-semibold italic border border-dashed border-gray-800 rounded-xl"
              >
                No subdomains match search or filter constraints.
              </motion.div>
            )}
          </motion.div>
        ) : (
          /* VIEW 2: TRADITIONAL DATA GRID TABLE */
          <motion.div
            key="table-mode"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-gray-850/45 border border-gray-800 rounded-xl overflow-hidden shadow-lg"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-800/40 border-b border-gray-800 text-[10px] font-bold text-gray-300 uppercase tracking-widest">
                    <th className="px-4 py-3">Subdomain</th>
                    <th className="px-4 py-3">IP Address</th>
                    <th className="px-4 py-3 text-center">HTTP Status</th>
                    <th className="px-4 py-3 text-center">Service</th>
                    <th className="px-4 py-3 text-center">Scan Method</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/40">
                  {paginatedSubdomains.length > 0 ? (
                    paginatedSubdomains.map((sub, index) => {
                      return (
                        <tr key={index} className="hover:bg-gray-850/60 transition-colors">
                          <td className="px-4 py-2.5 font-bold text-gray-200">
                            {sub.subdomain}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-gray-200">
                            {sub.ip}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`inline-block text-[10px] font-bold font-mono px-2 py-0.5 rounded ${getStatusColor(sub.http_code)}`}>
                              {sub.http_code ? `HTTP ${sub.http_code}` : "Resolved"}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-gray-900 border border-gray-700 text-gray-300 rounded font-mono">
                              {sub.service || "DNS"}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span className="text-[10px] font-bold text-gray-200 font-mono">
                              {sub.method}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-gray-200 font-medium italic">
                        No subdomains match search or filter constraints.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 text-xs bg-gray-850 border border-gray-800 rounded-lg text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition-colors font-bold"
          >
            Prev
          </button>

          <div className="px-3.5 py-1.5 text-xs font-mono font-bold text-gray-300 bg-gray-900 border border-gray-800 rounded-lg select-none">
            Page {currentPage} of {totalPages}
          </div>

          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 text-xs bg-gray-850 border border-gray-800 rounded-lg text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition-colors font-bold"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
